import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Play } from "lucide-react";
import { formatRupiah } from "@/lib/dirga";
import { STYLE_GROUPS } from "@/config/pricing";
import { SampleModal } from "@/components/dirga/SampleModal";
import { cn } from "@/lib/utils";

const Label = ({ children }) => (
  <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-[#8C6D32]">{children}</p>
);

const Includes = ({ list }) => (
  <div>
    <Label>{"What's included"}</Label>
    <ul className="mt-3 space-y-2">
      {list.map((t) => (
        <li key={t} className="flex gap-3 font-sans text-sm leading-relaxed text-[#5C5C5C]">
          <span className="mt-2.5 h-px w-3 shrink-0 bg-[#B9B2A4]" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const SelectableCard = ({ item, selected, onToggle, testId, styleId, onStyle }) => {
  const [open, setOpen] = useState(false); // detail add-on
  const [watching, setWatching] = useState(false); // jendela contoh video
  const addon = item.category === "Tambahan";
  const styles = item.styleGroup ? STYLE_GROUPS[item.styleGroup] : null;
  const includes = item.includes || [];

  return (
    <div
      className={cn(
        "relative self-start border transition-colors duration-300",
        selected ? "border-[#C6A56B] bg-[#FBF7EE] shadow-[0_18px_34px_-26px_rgba(198,165,107,0.9)]" : "border-[#E8E3D9] bg-white/90 hover:border-[#C6A56B]/70"
      )}
    >
      {/* bagian atas: ketuk untuk memilih / membatalkan */}
      <motion.button
        type="button"
        data-testid={testId}
        onClick={() => onToggle(item.id)}
        whileTap={{ scale: 0.995 }}
        aria-pressed={selected}
        className="group relative block w-full p-6 text-left outline-none focus-visible:ring-1 focus-visible:ring-[#1C1C1C]"
      >
        <span
          className={cn(
            "absolute right-4 top-4 flex h-6 w-6 items-center justify-center border transition-all duration-300",
            selected
              ? "border-[#C6A56B] bg-[#C6A56B] text-[#111111]"
              : "border-[#D8D2C6] bg-transparent text-transparent"
          )}
        >
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
        <div className="pr-8">
          <h4 className="font-sans text-base font-medium text-[#1C1C1C]">{item.name}</h4>
          {item.meta && (
            <p className="mt-1 font-sans text-xs uppercase tracking-wider text-[#8A8A8A]">{item.meta}</p>
          )}
          <p className="mt-4 font-serif text-2xl text-[#1C1C1C]">{formatRupiah(item.price)}</p>
        </div>
      </motion.button>

      {addon ? (
        includes.length > 0 && (
          <div className="px-6 pb-5">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-widest text-[#5C5C5C] hover:text-[#1C1C1C]"
            >
              {open ? "Hide details" : "View details"}
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
            </button>
            {open && (
              <div className="mt-4">
                <Includes list={includes} />
              </div>
            )}
          </div>
        )
      ) : (
        (includes.length > 0 || styles) && (
          <div className="mx-6 space-y-6 border-t border-[#E8E3D9] pb-6 pt-5">
            {includes.length > 0 && <Includes list={includes} />}

            {styles && (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>Choose a style</Label>
                  <button
                    type="button"
                    onClick={() => setWatching(true)}
                    className="inline-flex items-center gap-1.5 font-sans text-xs uppercase tracking-widest text-[#1C1C1C] underline"
                  >
                    <Play className="h-3 w-3" /> Watch samples
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {styles.map((st) => (
                    <button
                      key={st.id}
                      type="button"
                      aria-pressed={styleId === st.id}
                      data-testid={`style-${item.id}-${st.id}`}
                      onClick={() => onStyle && onStyle(item.id, st.id)}
                      className={cn(
                        "border px-3.5 py-2 font-sans text-xs transition-colors",
                        styleId === st.id
                          ? "border-[#C6A56B] bg-[#111111] text-white"
                          : "border-[#E8E3D9] bg-white text-[#5C5C5C] hover:border-[#C6A56B]/70"
                      )}
                    >
                      {st.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      )}

      <AnimatePresence>
        {watching && styles && (
          <SampleModal
            title={item.name}
            styles={styles}
            initialId={styleId}
            onClose={() => setWatching(false)}
            onPick={(id) => {
              onStyle && onStyle(item.id, id);
              setWatching(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
